import React from "react";
import { Controller, useForm } from "react-hook-form";
import { Button, Link, Stack, Typography, useMediaQuery } from "@mui/material";

import Image from "next/image";
import NextLink from "next/link";

import Banner from "../Common/banner";
import TextField from "components/CustomTextField";
import LanguageSelect from "components/LanguageSelect";

import BlueCaretLeft from "assets/icons/blue_carret_left.svg";
import { ROUTE_LOGIN, ROUTE_REGISTER } from "config/routes";
import images from "components/Authentication/ForgotPassword/images";
import PublicLayout from "components/Layout/Public";
import { ForgotPasswordForm } from "./styles";
import RobotImagePng from "assets/images/forgot_password_robot.png";
import { resetPassword } from "services/auth";
import { useSnackbar } from "notistack";
import { useIntl } from "react-intl";
import { yupResolver } from "@hookform/resolvers/yup";
import postForgotPasswordSchema from "validations/postForgotPassword";

export default function ForgotPassword() {
  const smallScreen = useMediaQuery("(max-height:620px)");
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(postForgotPasswordSchema),
    shouldUseNativeValidation: false,
    defaultValues: { email: "" },
  });

  let robotImageSize = {
    width: 137,
    height: 140,
  };

  if (smallScreen) {
    robotImageSize = {
      width: 80,
      height: 82,
    };
  }

  const onSubmit = async (data) => {
    try {
      const res = await resetPassword(data.email);
      enqueueSnackbar(
        intl.formatMessage({
          id: "We've sent an email to the address",
          defaultMessage: "We've sent an email to the address",
        })
      );
    } catch (e) {
      const { err_msg } = e.info || {};
      enqueueSnackbar(err_msg, { variant: "error" });
    }
  };

  return (
    <PublicLayout background={<Banner images={images} />}>
      <Stack>
        <Stack direction="row" justifyContent="flex-end">
          <LanguageSelect />
        </Stack>

        <Stack justifyContent="center" alignItems="center">
          <Typography component="p" variant="h4" fontWeight="bold">
            {intl.formatMessage({
              id: "Forgot password?",
              defaultMessage: "Forgot password?",
            })}
          </Typography>

          <NextLink href={ROUTE_LOGIN} passHref>
            <Button
              startIcon={<BlueCaretLeft />}
              color="secondary"
              component="a"
              variant="text"
            >
              {intl.formatMessage({
                id: "Back to login",
                defaultMessage: "Back to login",
              })}
            </Button>
          </NextLink>

          <Image src={RobotImagePng} {...robotImageSize} />

          <Typography component="p" variant="h4">
            {intl.formatMessage({
              id: "Don’t worry!",
              defaultMessage: "Don’t worry!",
            })}
          </Typography>

          <Typography variant="subtitle1">
            {intl.formatMessage({
              id: "Enter the email you used to register. We will send you a new password!",
              defaultMessage:
                "Enter the email you used to register. We will send you a new password!",
            })}
          </Typography>
        </Stack>
        <ForgotPasswordForm
          component="form"
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState: { error } }) => (
              <TextField
                label={intl.formatMessage({
                  id: "Enter Email",
                  defaultMessage: "Enter Email",
                })}
                type="email"
                placeholder="abc@email.com"
                error={error}
                {...field}
                autoFocus
                required
              />
            )}
          />

          <Button
            type="submit"
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={isSubmitting}
          >
            {intl.formatMessage({
              id: "Reset Password",
              defaultMessage: "Reset Password",
            })}
          </Button>
        </ForgotPasswordForm>

        <Stack
          mt={{ lg: 10 }}
          direction="row"
          justifyContent="center"
          spacing={0.5}
        >
          <Typography variant="body1" color="text.secondary">
            {intl.formatMessage({
              id: "New to GrabJobs?",
              defaultMessage: "New to GrabJobs?",
            })}
          </Typography>

          <NextLink href={ROUTE_REGISTER} passHref>
            <Link variant="body1" color="secondary">
              {intl.formatMessage({
                id: "Sign up now!",
                defaultMessage: "Sign up now!",
              })}
            </Link>
          </NextLink>
        </Stack>
      </Stack>
    </PublicLayout>
  );
}
